import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { FiSearch, FiBookOpen, FiPlusCircle, FiUser, FiSettings, FiEdit, FiLogOut, FiBookmark, FiMenu, FiX, FiMessageCircle, FiStar, FiHome, FiGrid, FiHeart, FiSun, FiMoon, FiGlobe } from "react-icons/fi";
import { MdAdminPanelSettings } from "react-icons/md";
import logo from "../../assets/images/logo/logo.webp";
import logoDark from "../../assets/images/logo/logo_dark.webp";
import { getFullImageUrl, searchAll, updateUserLanguage } from "../../services/api";
import { getAvatarImageUrl } from "../../utils/imageUtils";
import { getTokenData, removeToken } from "../../services/authService";
import { BiSearch } from 'react-icons/bi';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useAuthModal } from '../../context/AuthModalContext';
import { useDeletionStatus } from '../../hooks/useDeletionStatus';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from 'react-i18next';
import { trackRecipeSearch, trackUserLogin, trackUserRegistration } from '../../utils/analytics';

const LoginModal = lazy(() => import("../Modals/LoginModal"));
const RegisterModal = lazy(() => import("../Modals/RegisterModal"));
const SearchResults = lazy(() => import("../SearchResults/SearchResults"));
const NotificationBell = lazy(() => import("../NotificationBell/NotificationBell"));

// Локальний debounce, щоб не тягнути весь lodash у критичний бандл
const debounce = (fn, wait) => {
  let timeoutId;
  const debounced = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), wait);
  };
  debounced.cancel = () => clearTimeout(timeoutId);
  return debounced;
};

// Плейсхолдер тієї ж форми, що й кнопка дзвіночка, щоб уникнути зсуву layout (CLS)
const NotificationBellPlaceholder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    width: 34px;
    height: 34px;
    margin-right: 8px;
  }

  @media (max-width: 360px) {
    width: 28px;
    height: 28px;
    margin-right: 4px;
  }
`;


const DesktopCollectionsLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #ffffff;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.25);
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
    color: #ffffff;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderContainer = styled.header`
  background: ${({ theme }) =>
    theme === "dark"
      ? "#333"
      : "linear-gradient(90deg, #ff6f00 0%,rgb(255, 78, 0) 100%)"};
  padding: 6px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
  transition: background-color 0.3s ease;
  position: relative;
  z-index: ${({ $isMobileMenuOpen }) => $isMobileMenuOpen ? '997' : '1000'};
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: white;
  z-index: ${({ $isMobileMenuOpen }) => $isMobileMenuOpen ? '997' : '1001'};
  flex-shrink: 0;
  transition: opacity 0.3s ease;

  @media (max-width: 768px) {
    opacity: ${({ $isMobileMenuOpen }) => $isMobileMenuOpen ? '0.3' : '1'};
  }

  img {
    width: 85px;
    @media (max-width: 768px) {
      width: 65px;
    }
  }

  span {
    font-family: "Kalam", cursive;
    font-size: 1.8rem;
    font-weight: bold;
    @media (max-width: 768px) {
      font-size: 1.4rem;
    }
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 600px;
  margin: 0 20px;
  flex-grow: 1;

  @media (max-width: 1024px) {
    max-width: 400px;
  }

  @media (max-width: 768px) {
    display: ${({ $isMobileSearchVisible }) => $isMobileSearchVisible ? 'block' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin: 0;
    padding: 10px;
    background: ${({ theme }) => theme === "dark" ? "#333" : "#ff6f00"};
    z-index: 1000;
    max-width: 100%;
    width: 100vw;
    box-sizing: border-box;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 0 5px;
    box-sizing: border-box;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 2px solid ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 24px;
  background: ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#FFFFFF'};
  color: ${props => props.theme === 'dark' ? '#f1f5f9' : '#333'};
  font-size: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px ${props => props.theme === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)'};
  box-sizing: border-box;
  backdrop-filter: blur(10px);

  &:focus {
    outline: none;
    border-color: ${props => props.theme === 'dark' ? '#ff9800' : '#ff6f00'};
    background: ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.12)' : '#FFFFFF'};
    box-shadow: 0 4px 12px ${props => props.theme === 'dark' 
      ? 'rgba(255, 152, 0, 0.3)' 
      : 'rgba(255, 111, 0, 0.2)'};
  }

  &::placeholder {
    color: ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.4)'};
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 10px 16px 10px 40px;
    width: 100%;
  }
`;

const SearchIcon = styled(BiSearch)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)'};
  font-size: 22px;
  opacity: 0.8;
  transition: all 0.3s ease;
  z-index: 10;
  pointer-events: none;

  ${SearchInputWrapper}:hover & {
    opacity: 1;
    color: ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)'};
  }

  @media (max-width: 768px) {
    font-size: 18px;
    left: 20px;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 15px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileControls = styled.div`
  display: none;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  
  @media (max-width: 768px) {
    display: flex;
  }
  
  @media (max-width: 360px) {
    gap: 4px;
  }
`;

const MobileNav = styled.nav`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0; /* Завжди справа, AnimatePresence контролює x позицію */
    width: 85%;
    max-width: 340px; /* Трохи ширше для кращого вигляду */
    height: 100vh;
    height: 100dvh;
    background: ${({ theme }) => theme === "dark" 
      ? "linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%)"
      : "linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)"};
    box-shadow: ${({ theme }) => theme === "dark"
      ? "-8px 0 32px rgba(0, 0, 0, 0.6)"
      : "-8px 0 32px rgba(0, 0, 0, 0.2)"}; /* Більш виразна тінь */
    /* Видаляємо CSS transition - використовуємо тільки Framer Motion */
    z-index: 999;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    /* Запобігаємо scroll chaining */
    overscroll-behavior: contain;
    -ms-scroll-chaining: none;
    touch-action: pan-y;
    border-left: 1px solid ${({ theme }) => theme === "dark" 
      ? "rgba(255, 255, 255, 0.1)" 
      : "rgba(0, 0, 0, 0.1)"};
    /* Максимальна GPU оптимізація */
    will-change: transform;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    perspective: 1000px;
    /* Додаємо внутрішні відступи для кращого вигляду */
    padding-bottom: 20px;
  }
`;

const MobileNavOverlay = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block; /* Завжди показуємо, AnimatePresence контролює mount/unmount */
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    z-index: 998;
    /* Оптимізація для GPU */
    will-change: opacity;
    transform: translateZ(0);
    cursor: pointer; /* Додаємо cursor pointer для кращого UX */
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 50%;
  /* Оптимізація для touch */
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
  min-width: 44px; /* Мінімальний розмір для touch targets */
  min-height: 44px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }
  
  /* Мобільні touch стилі */
  @media (hover: none) and (pointer: coarse) {
    &:hover {
      background: none;
    }
    
    &:active {
      background: rgba(255, 255, 255, 0.15);
      transform: scale(0.9);
    }
  }
`;

const MobileMenuItem = styled.div`
  padding: 12px 20px; /* Зменшені відступи */
  color: ${({ theme }) => theme === "dark" ? "#f1f5f9" : "#333"};
  display: flex;
  align-items: center;
  gap: 12px; /* Зменшений проміжок */
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px; /* Менші кути */
  margin: 2px 16px; /* Зменшені відступи між елементами */
  font-weight: 500;
  font-size: 15px; /* Трохи менший шрифт */
  position: relative;
  /* Оптимізація для GPU */
  will-change: transform, background-color;
  transform: translateZ(0);
  min-height: 44px; /* Зменшена висота */
  
  &:hover {
    background: ${({ theme }) => theme === "dark" 
      ? "linear-gradient(135deg, rgba(255, 107, 0, 0.12), rgba(255, 107, 0, 0.06))" 
      : "linear-gradient(135deg, rgba(255, 107, 0, 0.1), rgba(255, 107, 0, 0.05))"};
    color: #ff6b00;
    transform: translateX(6px) translateZ(0);
    box-shadow: ${({ theme }) => theme === "dark"
      ? "0 4px 12px rgba(255, 107, 0, 0.15)"
      : "0 4px 12px rgba(255, 107, 0, 0.1)"};
  }

  &:active {
    transform: translateX(3px) scale(0.98) translateZ(0);
  }

  svg {
    font-size: 20px; /* Зменшені іконки */
    color: ${({ theme }) => theme === "dark" ? "#94a3b8" : "#666"};
    transition: all 0.2s ease;
    /* Оптимізація іконок */
    will-change: color, transform;
    min-width: 20px; /* Фіксована ширина для вирівнювання */
  }

  &:hover svg {
    color: #ff6b00;
    transform: scale(1.1);
  }
  
  /* Додаємо індикатор активності */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px; /* Трохи ширше */
    height: 0;
    background: linear-gradient(180deg, #ff6b00, #ff8e3c);
    border-radius: 0 4px 4px 0;
    transition: height 0.2s ease;
    will-change: height;
  }
  
  &:hover::before {
    height: 70%; /* Трохи вище */
  }
  
  /* Мобільні touch стилі */
  @media (hover: none) and (pointer: coarse) {
    &:hover {
      transform: translateX(0);
      box-shadow: none;
      background: transparent;
      color: ${({ theme }) => theme === "dark" ? "#f1f5f9" : "#333"};
    }
    
    &:hover svg {
      transform: none;
      color: ${({ theme }) => theme === "dark" ? "#94a3b8" : "#666"};
    }
    
    &:active {
      background: ${({ theme }) => theme === "dark" 
        ? "rgba(255, 107, 0, 0.15)" 
        : "rgba(255, 107, 0, 0.1)"};
      color: #ff6b00;
      transform: scale(0.95);
    }
    
    &:active svg {
      color: #ff6b00;
    }
  }
`;

const MobileMenuDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme === "dark" 
    ? "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)"
    : "linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.1), transparent)"};
  margin: 16px 20px; /* Зменшені відступи */
`;

const MobileMenuSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 4px; /* Зменшений відступ */
`;

const AnimatedMenuContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

// Variants для плавного закриття
const containerVariants = {
  hidden: { 
    opacity: 0,
    transition: {
      staggerChildren: 0.02,
      staggerDirection: -1,
      when: "afterChildren"
    }
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
      delayChildren: 0.1,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { 
    opacity: 0, 
    x: 20,
    transition: { duration: 0.1 }
  },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.15 }
  }
};

const MobileMenuHeader = styled.div`
  padding: 20px 20px 16px; /* Зменшені відступи */
  border-bottom: 1px solid ${({ theme }) => theme === "dark" 
    ? "rgba(255, 255, 255, 0.1)" 
    : "rgba(0, 0, 0, 0.1)"};
  margin-bottom: 16px; /* Зменшений margin */
`;

const MobileUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  cursor: pointer; /* Додаємо cursor pointer */
  padding: 8px; /* Додаємо padding для кращої області кліку */
  border-radius: 12px; /* Округлі кути */
  transition: all 0.2s ease;
  /* Оптимізація для touch */
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
  flex-shrink: 0;
  
  /* Hover ефекти */
  &:hover {
    background: ${({ theme }) => theme === "dark" 
      ? "rgba(255, 107, 0, 0.08)" 
      : "rgba(255, 107, 0, 0.05)"};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0) scale(0.98);
  }
  
  img {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #ff6b00;
    flex-shrink: 0;
    transition: all 0.3s ease;
  }
  
  /* Hover ефект для аватарки */
  &:hover img {
    transform: scale(1.05);
    border-color: #ff8e3c;
    box-shadow: 0 4px 12px rgba(255, 107, 0, 0.3);
  }
  
  /* Мобільні touch стилі */
  @media (hover: none) and (pointer: coarse) {
    &:hover {
      background: transparent;
      transform: none;
    }
    
    &:hover img {
      transform: none;
      box-shadow: none;
    }
    
    &:active {
      background: ${({ theme }) => theme === "dark" 
        ? "rgba(255, 107, 0, 0.12)" 
        : "rgba(255, 107, 0, 0.08)"};
      transform: scale(0.95);
    }
    
    &:active img {
      transform: scale(1.02);
    }
  }
`;

const MobileUserDetails = styled.div`
  flex: 1;
  transition: all 0.2s ease;
  
  h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: ${({ theme }) => theme === "dark" ? "#f1f5f9" : "#333"};
    line-height: 1.2;
    transition: color 0.2s ease;
  }
  
  p {
    margin: 4px 0 0;
    font-size: 13px;
    color: ${({ theme }) => theme === "dark" ? "#94a3b8" : "#666"};
    opacity: 0.8;
    font-weight: 400;
    transition: color 0.2s ease;
  }
  
  /* Hover ефекти через батьківський елемент */
  ${MobileUserInfo}:hover & h3 {
    color: #ff6b00;
  }
  
  ${MobileUserInfo}:hover & p {
    color: ${({ theme }) => theme === "dark" ? "#ff8e3c" : "#ff6b00"};
    opacity: 1;
  }
  
  /* Мобільні touch стилі */
  @media (hover: none) and (pointer: coarse) {
    ${MobileUserInfo}:hover & h3 {
      color: ${({ theme }) => theme === "dark" ? "#f1f5f9" : "#333"};
    }
    
    ${MobileUserInfo}:hover & p {
      color: ${({ theme }) => theme === "dark" ? "#94a3b8" : "#666"};
      opacity: 0.8;
    }
    
    ${MobileUserInfo}:active & h3 {
      color: #ff6b00;
    }
    
    ${MobileUserInfo}:active & p {
      color: #ff8e3c;
      opacity: 1;
    }
  }
`;

const MobileSectionTitle = styled.h4`
  margin: 0 0 8px; /* Зменшений відступ */
  padding: 0 20px; /* Зменшений padding */
  font-size: 12px; /* Менший шрифт */
  font-weight: 600; /* Менша жирність */
  text-transform: uppercase;
  letter-spacing: 1px; /* Менший letter-spacing */
  color: ${({ theme }) => theme === "dark" ? "#94a3b8" : "#666"}; /* Повертаємо нейтральний колір */
`;

const MobileQuickActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px; /* Зменшений проміжок */
  padding: 0 20px; /* Зменшений padding */
  margin-bottom: 16px; /* Зменшений margin */
`;

const MobileQuickAction = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 14px 12px; /* Зменшені відступи */
  background: ${({ theme }) => theme === "dark" 
    ? "linear-gradient(135deg, rgba(255, 107, 0, 0.12), rgba(255, 107, 0, 0.06))" 
    : "linear-gradient(135deg, rgba(255, 107, 0, 0.1), rgba(255, 107, 0, 0.05))"};
  border: 1px solid ${({ theme }) => theme === "dark" 
    ? "rgba(255, 107, 0, 0.25)" 
    : "rgba(255, 107, 0, 0.2)"};
  border-radius: 16px; /* Більш округлі кути */
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  /* Оптимізація для touch */
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
  min-height: 64px; /* Зменшена висота */
  
  svg {
    font-size: 22px; /* Зменшені іконки */
    color: #ff6b00;
    /* Оптимізація іконок */
    will-change: transform;
    transition: transform 0.2s ease;
  }
  
  span {
    font-size: 12px; /* Зменшений текст */
    font-weight: 600;
    color: ${({ theme }) => theme === "dark" ? "#f1f5f9" : "#333"};
    text-align: center;
    line-height: 1.2;
  }
  
  &:hover {
    background: ${({ theme }) => theme === "dark" 
      ? "linear-gradient(135deg, rgba(255, 107, 0, 0.18), rgba(255, 107, 0, 0.1))" 
      : "linear-gradient(135deg, rgba(255, 107, 0, 0.15), rgba(255, 107, 0, 0.08))"};
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme === "dark"
      ? "0 6px 20px rgba(255, 107, 0, 0.2)"
      : "0 6px 20px rgba(255, 107, 0, 0.15)"};
  }
  
  &:hover svg {
    transform: scale(1.15);
  }
  
  &:active {
    transform: translateY(-1px) scale(0.98);
  }
  
  /* Мобільні touch стилі */
  @media (hover: none) and (pointer: coarse) {
    &:hover {
      transform: none;
      box-shadow: none;
      background: ${({ theme }) => theme === "dark" 
        ? "linear-gradient(135deg, rgba(255, 107, 0, 0.12), rgba(255, 107, 0, 0.06))" 
        : "linear-gradient(135deg, rgba(255, 107, 0, 0.1), rgba(255, 107, 0, 0.05))"};
    }
    
    &:hover svg {
      transform: none;
    }
    
    &:active {
      background: ${({ theme }) => theme === "dark" 
        ? "linear-gradient(135deg, rgba(255, 107, 0, 0.25), rgba(255, 107, 0, 0.15))" 
        : "linear-gradient(135deg, rgba(255, 107, 0, 0.2), rgba(255, 107, 0, 0.12))"};
      transform: scale(0.95);
    }
    
    &:active svg {
      transform: scale(1.1);
    }
  }
`;

const MobileControlsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 0 20px;
  margin-bottom: 20px;
`;

const MobileControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${({ theme, active }) => 
    active 
      ? (theme === "dark" ? "#ff6b00" : "#ff6b00")
      : (theme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)")};
  border: 1px solid ${({ theme, active }) => 
    active 
      ? "#ff6b00"
      : (theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)")};
  border-radius: 10px;
  color: ${({ theme, active }) => 
    active 
      ? "#fff"
      : (theme === "dark" ? "#f1f5f9" : "#333")};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ theme, active }) => 
      active 
        ? "#e55a00"
        : (theme === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)")};
  }
  
  svg {
    font-size: 18px;
  }
`;

const MobileLanguageSelect = styled.select`
  background: ${({ theme }) => theme === "dark" 
    ? "rgba(255, 255, 255, 0.05)" 
    : "rgba(0, 0, 0, 0.05)"};
  border: 1px solid ${({ theme }) => theme === "dark" 
    ? "rgba(255, 255, 255, 0.1)" 
    : "rgba(0, 0, 0, 0.1)"};
  color: ${({ theme }) => theme === "dark" ? "#f1f5f9" : "#333"};
  border-radius: 10px;
  padding: 12px 32px 12px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${({ theme }) => theme === "dark" ? "%23f1f5f9" : "%23333"}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;

  &:focus {
    outline: none;
    border-color: #ff6b00;
    background: ${({ theme }) => theme === "dark" 
      ? "rgba(255, 107, 0, 0.1)" 
      : "rgba(255, 107, 0, 0.08)"};
  }

  option {
    background-color: ${({ theme }) => theme === "dark" ? "#2d2d2d" : "#fff"};
    color: ${({ theme }) => theme === "dark" ? "#f1f5f9" : "#333"};
    padding: 8px;
  }
`;



const MobileLogoutButton = styled.button`
  padding: 14px 20px; /* Зменшені відступи */
  margin: 16px 20px 16px; /* Зменшені відступи */
  width: calc(100% - 40px);
  background: ${({ theme }) => theme === "dark" 
    ? "linear-gradient(135deg, rgba(239, 68, 68, 0.12), rgba(220, 38, 38, 0.08))" 
    : "linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.06))"};
  border: 1px solid ${({ theme }) => theme === "dark" 
    ? "rgba(239, 68, 68, 0.25)" 
    : "rgba(239, 68, 68, 0.2)"};
  border-radius: 12px; /* Менші кути */
  color: #ef4444;
  font-size: 15px; /* Менший шрифт */
  font-weight: 500; /* Менша жирність */
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px; /* Менший gap */
  box-sizing: border-box;
  /* Оптимізація для touch */
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
  min-height: 48px; /* Зменшена висота */
  
  &:hover {
    background: ${({ theme }) => theme === "dark" 
      ? "linear-gradient(135deg, rgba(239, 68, 68, 0.18), rgba(220, 38, 38, 0.12))" 
      : "linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.1))"};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme === "dark"
      ? "0 4px 12px rgba(239, 68, 68, 0.2)"
      : "0 4px 12px rgba(239, 68, 68, 0.15)"};
  }
  
  &:active {
    transform: translateY(0) scale(0.98);
  }
  
  svg {
    font-size: 22px;
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: scale(1.1);
  }
  
  /* Мобільні touch стилі */
  @media (hover: none) and (pointer: coarse) {
    &:hover {
      transform: none;
      box-shadow: none;
      background: ${({ theme }) => theme === "dark" 
        ? "linear-gradient(135deg, rgba(239, 68, 68, 0.12), rgba(220, 38, 38, 0.08))" 
        : "linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.06))"};
    }
    
    &:active {
      background: ${({ theme }) => theme === "dark" 
        ? "linear-gradient(135deg, rgba(239, 68, 68, 0.25), rgba(220, 38, 38, 0.18))" 
        : "linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.15))"};
      transform: scale(0.95);
    }
  }
`;

const LanguageSelect = styled.select`
  background: none;
  border: 1px solid white;
  color: white;
  border-radius: 5px;
  padding: 5px;
  font-size: 1rem;
  font-family: "Inter", sans-serif;

  &:focus {
    outline: none;
  }

  option {
    color: black;
    background-color: white;
  }
`;

const ThemeToggle = styled.div`
  cursor: pointer;
  font-size: 1.5rem;
  transition: color 0.3s ease;
  user-select: none;

  &:hover {
    color: ${({ theme }) => (theme === "dark" ? "#ff6f00" : "#333")};
  }
`;

const ModalButton = styled.button`
  background: none;
  color: white;
  font-family: "Inter", sans-serif;
  font-weight: bold;
  font-size: 16px;
  border: none;
  cursor: pointer;
  position: relative;
  display: inline-block;

  &:after {
    content: "";
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: white;
    transform: scaleX(0);
    transition: transform 0.3s ease, background-color 0.3s ease;
  }

  &:hover {
    text-decoration: none;

    &:after {
      transform: scaleX(1);
      background-color: #ffeb3b;
    }
  }
`;

const DropdownMenu = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownContent = styled.ul`
  position: absolute;
  inset: 0px auto auto 0px;
  margin: 0px;
  transform: translate(-110px, 34px);
  background-color: ${props => props.theme === 'dark' ? '#2c2c2c' : '#fff'};
  min-width: 200px;
  box-shadow: ${props => props.theme === 'dark'
    ? '0 8px 32px rgba(0, 0, 0, 0.4)'
    : '0 5px 25px rgba(0, 0, 0, 0.1)'};
  z-index: 1;
  border-radius: 12px;
  display: ${({ $show }) => ($show ? "block" : "none")};
  padding: 8px 0;
  border: 1px solid ${props => props.theme === 'dark'
    ? 'rgba(255, 255, 255, 0.1)'
    : 'rgba(0, 0, 0, 0.1)'};
  overflow: hidden;
`;

const DropdownItem = styled.li`
  list-style: none;
  cursor: pointer;
  font-family: "Inter", sans-serif;

  a, button {
    color: ${props => props.theme === 'dark' ? '#f1f5f9' : '#333'};
    text-decoration: none;
    display: flex;
    width: 100%;
    padding: 10px 20px;
    background: none;
    border: none;
    text-align: left;
    font-size: 14px;
    font-family: inherit;
    font-weight: bold;
    cursor: pointer;
    align-items: center;
    gap: 10px;
    transition: all 0.2s ease;
    
    svg {
      font-size: 18px;
      color: ${props => props.theme === 'dark' ? '#94a3b8' : '#666'};
    }
    
    &:hover {
      color: #ff6f00;
      background-color: ${props => props.theme === 'dark'
    ? 'rgba(255, 107, 0, 0.1)'
    : '#fff5e6'};
      
      svg {
        color: #ff6f00;
      }
    }
  }
`;

const Divider = styled.hr`
  margin: 8px 0;
  border: none;
  border-top: 1px solid ${props => props.theme === 'dark'
    ? 'rgba(255, 255, 255, 0.1)'
    : 'rgba(0, 0, 0, 0.1)'};
`;

const AvatarButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  flex: 0 0 auto;
  flex-shrink: 0;

  img {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    border-radius: 50%;
    object-fit: cover;
    display: block;
    flex-shrink: 0;
    transition: all 0.3s ease;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    
    img {
      transform: scale(1.1);
      box-shadow: 0 0 0 2px #ffeb3b;
    }
  }
`;

const AdminButton = styled(DropdownItem)`
  background-color: ${props => props.theme === 'dark' ? '#2c2c2c' : '#fff0e6'};
  border-bottom: 1px solid ${props => props.theme === 'dark' ? '#3d3d3d' : '#ffe4d4'};
  
  a {
    color: ${props => props.theme === 'dark' ? '#ff6f00' : '#d35400'};
    font-weight: 600;
    
    &:hover {
      background-color: ${props => props.theme === 'dark' ? '#3d3d3d' : '#ffe4d4'};
    }
  }
`;

const PremiumButton = styled(AdminButton)`
  background-color: ${props => props.theme === 'dark' ? '#2c2c2c' : '#fff7e6'};
  border-bottom: 1px solid ${props => props.theme === 'dark' ? '#3d3d3d' : '#ffe4d4'};
  
  a {
    color: #ffc107;
    font-weight: 600;
    
    &:hover {
      background-color: ${props => props.theme === 'dark' ? '#3d3d3d' : '#fff0d4'};
    }
  }
`;

const Header = () => {
  const { currentLanguage, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef(null);
  const { showLoginModal, showRegisterModal, openLoginModal, openRegisterModal, closeModals } = useAuthModal();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userAvatar, setUserAvatar] = useState(null);
  const token = localStorage.getItem("token");
  const userHandle = localStorage.getItem('userHandle');
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUserData, user } = useAuth();
  const currentAvatar = user?.avatarUrl || userAvatar;
  const avatarSrc = getAvatarImageUrl(currentAvatar, 64);
  const currentUserHandle = user?.userHandle || userHandle;
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const profileSegmentIndex = pathSegments.indexOf('profile');
  const viewedProfileHandle = profileSegmentIndex >= 0 ? pathSegments[profileSegmentIndex + 1] : null;

  // Додаємо перевірку статусу видалення
  const { deletionStatus } = useDeletionStatus();
  const dropdownRef = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchVisible, setIsMobileSearchVisible] = useState(false);

  const debouncedSearch = useRef(
    debounce(async (query) => {
      if (query.length >= 2) {
        setIsLoading(true);
        try {
          const results = await searchAll(query);
          setSearchResults(results);
          setIsSearching(true);
        } catch (error) {
          console.error('Помилка пошуку:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSearchResults(null);
        setIsSearching(false);
      }
    }, 300)
  ).current;

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Очищаємо стилі body при розмонтуванні або зміні стану меню
  useEffect(() => {
    return () => {
      // Очищаємо стилі при розмонтуванні компонента
      const scrollY = document.body.getAttribute('data-scroll-y');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY));
        document.body.removeAttribute('data-scroll-y');
      }
    };
  }, []);

  // Обробляємо зміну розміру екрану
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        // Відновлюємо скрол при зміні розміру екрану
        const scrollY = document.body.getAttribute('data-scroll-y');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        if (scrollY) {
          window.scrollTo(0, parseInt(scrollY));
          document.body.removeAttribute('data-scroll-y');
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Додаткова обробка touch events для запобігання scroll chaining
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const preventBodyScroll = (e) => {
      // Перевіряємо чи touch event відбувається в межах мобільного меню
      const mobileNav = document.querySelector('[data-mobile-nav="true"]');
      if (!mobileNav) return;

      const isInsideMenu = mobileNav.contains(e.target);
      const isOverlay = e.target.closest('[data-mobile-overlay="true"]');
      
      // Не блокуємо події на overlay, щоб клік працював
      if (!isInsideMenu && !isOverlay) {
        e.preventDefault();
        return;
      }
      
      // Якщо це overlay, не обробляємо далі
      if (isOverlay) {
        return;
      }

      // Якщо touch в межах меню, перевіряємо чи досягнуто меж скролу
      const { scrollTop, scrollHeight, clientHeight } = mobileNav;
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight;

      // Визначаємо напрямок скролу
      const touchY = e.touches[0].clientY;
      const deltaY = touchY - (e.target.dataset.lastTouchY || touchY);
      e.target.dataset.lastTouchY = touchY;

      // Запобігаємо скролу body якщо досягнуто меж меню
      if ((isAtTop && deltaY > 0) || (isAtBottom && deltaY < 0)) {
        e.preventDefault();
      }
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 1) {
        e.target.dataset.lastTouchY = e.touches[0].clientY;
      }
    };

    // Додаємо обробники
    document.addEventListener('touchmove', preventBodyScroll, { passive: false });
    document.addEventListener('touchstart', handleTouchStart, { passive: true });

    return () => {
      document.removeEventListener('touchmove', preventBodyScroll);
      document.removeEventListener('touchstart', handleTouchStart);
    };
  }, [isMobileMenuOpen]);

  // Очищаємо пошук при зміні маршруту
  useEffect(() => {
    setSearchQuery('');
    setSearchResults(null);
    setIsSearching(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearching(false);
      }
      if (dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest('.avatar-button')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleTokenChange = () => {
      const tokenData = getTokenData();
      if (tokenData && tokenData.avatarUrl) {
        setUserAvatar(tokenData.avatarUrl);
      }
    };

    // Додаємо слухач події
    window.addEventListener('storage', handleTokenChange);

    // Початкова ініціалізація
    handleTokenChange();

    // Прибираємо слухач при розмонтуванні
    return () => {
      window.removeEventListener('storage', handleTokenChange);
    };
  }, []);

  useEffect(() => {
    if (user?.avatarUrl) {
      setUserAvatar(user.avatarUrl);
    }
  }, [user?.avatarUrl]);

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (searchQuery.length >= 2) {
      setIsLoading(true);
      try {
        const results = await searchAll(searchQuery);
        setSearchResults(results);
        setIsSearching(true);
        
        // Відстеження пошуку
        const totalResults = (results.recipes?.length || 0) + (results.users?.length || 0);
        trackRecipeSearch(searchQuery, totalResults);
      } catch (error) {
        console.error('Помилка пошуку:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleLanguageChange = async (e) => {
    e.preventDefault();
    const newLang = e.target.value;
    if (newLang !== currentLanguage) {
      // Змінюємо мову в контексті та localStorage
      await changeLanguage(newLang);

      // Зберігаємо мову в профілі, лише якщо користувач авторизований (явна дія)
      if (getTokenData()) {
        try {
          await updateUserLanguage(newLang);
        } catch (error) {
          console.error('Не вдалося зберегти мову користувача:', error);
        }
      }

      // Оновлюємо URL з новою мовою
      const currentPath = location.pathname;
      const pathSegments = currentPath.split('/').filter(Boolean);
      
      // Перевіряємо чи перший сегмент - це мова
      if (pathSegments.length > 0 && ['uk', 'en'].includes(pathSegments[0])) {
        // Замінюємо мову в URL
        pathSegments[0] = newLang;
        const newPath = '/' + pathSegments.join('/') + location.search;
        navigate(newPath, { replace: true });
      } else {
        // Додаємо мову до початку URL
        const newPath = `/${newLang}${currentPath}${location.search}`;
        navigate(newPath, { replace: true });
      }
    }
  };

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleSignOut = () => {
    removeToken();
    updateUserData();
    navigate('/');

    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const handleMenuItemClick = useCallback((tab) => {
    if (!user?.userHandle) {
      console.error('Користувач не авторизований або userHandle відсутній');
      return;
    }

    setIsDropdownOpen(false);

    if (tab === 'profile') {
      navigate(`/${currentLanguage}/profile/${user.userHandle}`);
    } else if (tab === 'settings' || tab === 'saved') {
      navigate(`/${currentLanguage}/profile/${user.userHandle}`, {
        state: { activeTab: tab }
      });
    }
  }, [navigate, currentLanguage, user?.userHandle]);

  const handleLoginClick = () => {
    openLoginModal();
  };

  const handleRegisterClick = () => {
    openRegisterModal();
  };

  const toggleMobileMenu = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    
    // Блокуємо скрол body тільки коли меню відкрите
    if (newState) {
      // Зберігаємо поточну позицію скролу
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      // Зберігаємо позицію для відновлення
      document.body.setAttribute('data-scroll-y', scrollY.toString());
    } else {
      // Відновлюємо скрол
      const scrollY = document.body.getAttribute('data-scroll-y');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY));
        document.body.removeAttribute('data-scroll-y');
      }
    }
    
    if (isMobileSearchVisible) {
      setIsMobileSearchVisible(false);
    }
  };

  const toggleMobileSearch = () => {
    setIsMobileSearchVisible(!isMobileSearchVisible);
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
      // Відновлюємо скрол
      const scrollY = document.body.getAttribute('data-scroll-y');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY));
        document.body.removeAttribute('data-scroll-y');
      }
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    // Відновлюємо скрол
    const scrollY = document.body.getAttribute('data-scroll-y');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY));
      document.body.removeAttribute('data-scroll-y');
    }
  };

  const handleSupportClick = () => {
    navigate(`/${currentLanguage}/support`);
    closeMobileMenu();
  };

  const handleMobileNavigation = (path) => {
    navigate(`/${currentLanguage}${path}`);
    closeMobileMenu();
  };

  return (
    <HeaderContainer theme={theme} $isMobileMenuOpen={isMobileMenuOpen}>
      <HeaderContent>
        <LogoContainer to="/" $isMobileMenuOpen={isMobileMenuOpen}>
          <img 
            src={theme === 'dark' ? logoDark : logo} 
            alt="Cookly Logo" 
            width="85" 
            height="58"
            loading="eager"
            decoding="async"
          />
          <span>Cookly</span>
        </LogoContainer>

        <SearchWrapper theme={theme} $isMobileSearchVisible={isMobileSearchVisible}>
          <form autoComplete="off" onSubmit={handleSearchSubmit}>
            {/* Прихований input для обману браузера */}
            <input
              type="email"
              name="fake-email"
              autoComplete="email"
              style={{ display: 'none' }}
              tabIndex="-1"
            />
            <SearchInputWrapper>
              <SearchIcon theme={theme} />
              <SearchInput
                theme={theme}
                type="search"
                name="recipe-search-query"
                id="recipe-search-input"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-lpignore="true"
                data-form-type="other"
                placeholder={t('navigation.search')}
                value={searchQuery}
                onChange={handleSearchInputChange}
                onFocus={() => setIsSearching(true)}
                key={location.pathname}
              />
            </SearchInputWrapper>
          </form>
          {searchResults && (
            <Suspense fallback={null}>
              <SearchResults
                results={searchResults}
                searchQuery={searchQuery}
                onClose={() => setSearchResults(null)}
                theme={theme}
              />
            </Suspense>
          )}
        </SearchWrapper>

        <Nav theme={theme}>
          {token ? (
            <>
              <Suspense fallback={<NotificationBellPlaceholder />}>
                <NotificationBell />
              </Suspense>
              <DropdownMenu ref={dropdownRef}>
                <AvatarButton onClick={handleDropdownToggle} className="avatar-button">
                  <img
                    src={avatarSrc}
                    alt={t('profile.avatar')}
                    width="32"
                    height="32"
                    className="rounded-circle"
                    onError={(e) => {
                      const fallback = getAvatarImageUrl('avatars/default/default.webp');
                      if (e.target.src !== fallback) {
                        e.target.src = fallback;
                      }
                    }}
                  />
                </AvatarButton>
                <DropdownContent $show={isDropdownOpen} theme={theme}>
                  {user && (user.roleId === 4 || user.roleId === 3) && (
                    <>
                      <AdminButton theme={theme}>
                        <Link to={`/${currentLanguage}/admin`}>
                          <MdAdminPanelSettings />
                          {t('navigation.adminPanel')}
                        </Link>
                      </AdminButton>
                    </>
                  )}
                  {user && (
                    <PremiumButton theme={theme}>
                      <Link to={`/${currentLanguage}/premium`} onClick={() => setIsDropdownOpen(false)}>
                        <FiStar />
                        Premium
                      </Link>
                    </PremiumButton>
                  )}
                  <DropdownItem theme={theme}>
                    <button onClick={() => handleMenuItemClick('profile')}>
                      <FiUser />
                      {t('navigation.profile')}
                    </button>
                  </DropdownItem>
                  <DropdownItem theme={theme}>
                    <button onClick={() => handleMenuItemClick('saved')}>
                      <FiBookmark />
                      {t('navigation.savedRecipes')}
                    </button>
                  </DropdownItem>
                  <DropdownItem theme={theme}>
                    <Link to={`/${currentLanguage}/add-recipe`} onClick={() => setIsDropdownOpen(false)}>
                      <FiPlusCircle />
                      {t('navigation.addRecipe')}
                    </Link>
                  </DropdownItem>
                  <Divider theme={theme} />
                  <DropdownItem theme={theme}>
                    <button onClick={() => handleMenuItemClick('settings')}>
                      <FiSettings />
                      {t('navigation.settings')}
                    </button>
                  </DropdownItem>
                  <DropdownItem theme={theme}>
                    <Link to={`/${currentLanguage}/support`} onClick={() => setIsDropdownOpen(false)}>
                      <FiEdit />
                      {t('navigation.support')}
                    </Link>
                  </DropdownItem>
                  <Divider theme={theme} />
                  <DropdownItem theme={theme}>
                    <button onClick={handleSignOut}>
                      <FiLogOut />
                      {t('common.signOut')}
                    </button>
                  </DropdownItem>
                </DropdownContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <ModalButton onClick={handleLoginClick} theme={theme}>{t('navigation.login')}</ModalButton>
              <ModalButton onClick={handleRegisterClick} theme={theme}>{t('navigation.register')}</ModalButton>
            </>
          )}

          <DesktopCollectionsLink to={`/${currentLanguage}/collections`} theme={theme}>
            <FiBookOpen size={16} />
            <span>{currentLanguage === 'uk' ? 'Добірки' : 'Collections'}</span>
          </DesktopCollectionsLink>
          <DesktopCollectionsLink to={`/${currentLanguage}/meal-planner`} theme={theme}>
            <span style={{ fontSize: 15, lineHeight: 1 }}>📅</span>
            <span>{currentLanguage === 'uk' ? 'Меню на 7 днів' : 'Meal Planner'}</span>
          </DesktopCollectionsLink>
          <LanguageSelect
            value={currentLanguage}
            onChange={handleLanguageChange}
            theme={theme}
            aria-label={t('header.selectLanguage') || 'Select language'}
          >
            <option value="uk">UA</option>
            <option value="en">EN</option>
          </LanguageSelect>
          <ThemeToggle theme={theme} onClick={toggleTheme}>
            {theme === "light" ? "🌙" : "☀️"}
          </ThemeToggle>
        </Nav>

        <MobileControls theme={theme}>
          <IconButton 
            onClick={toggleMobileSearch} 
            theme={theme}
            aria-label={t('header.search') || 'Пошук'}
            aria-expanded={isMobileSearchVisible}
          >
            <FiSearch />
          </IconButton>
          {token && (
            <Suspense fallback={null}>
              <NotificationBell />
            </Suspense>
          )}
          <IconButton 
            onClick={toggleMobileMenu} 
            theme={theme}
            aria-label={isMobileMenuOpen ? (t('header.closeMenu') || 'Закрити меню') : (t('header.openMenu') || 'Відкрити меню')}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <FiX /> : <FiMenu />}
          </IconButton>
        </MobileControls>
      </HeaderContent>

      {isMobileMenuOpen && (
          <MobileNavOverlay
            data-mobile-overlay="true"
            onClick={(e) => {
              e.stopPropagation();
              closeMobileMenu();
            }}
            theme={theme}
          />
        )}

      {isMobileMenuOpen && (
          <MobileNav
            isOpen={isMobileMenuOpen}
            theme={theme}
            data-mobile-nav="true"
          >
        {user ? (
          <>
            {/* User Header */}
            <MobileMenuHeader theme={theme}>
              <MobileUserInfo 
                theme={theme}
                onClick={() => { 
                  handleMenuItemClick('profile'); 
                  closeMobileMenu(); 
                }}
              >
                <img
                  src={avatarSrc}
                  alt={t('profile.avatar')}
                  onError={(e) => {
                    const fallback = getAvatarImageUrl('avatars/default/default.webp');
                    if (e.target.src !== fallback) {
                      e.target.src = fallback;
                    }
                  }}
                />
                <MobileUserDetails theme={theme}>
                  <h3>{user.username || user.email}</h3>
                  <p>{user.email}</p>
                </MobileUserDetails>
              </MobileUserInfo>
              
              {/* Quick Actions */}
              <MobileQuickActions>
                <MobileQuickAction theme={theme} onClick={() => handleMobileNavigation('/add-recipe')}>
                  <FiPlusCircle />
                  <span>{t('navigation.addRecipe')}</span>
                </MobileQuickAction>
                <MobileQuickAction theme={theme} onClick={() => { handleMenuItemClick('saved'); closeMobileMenu(); }}>
                  <FiBookmark />
                  <span>{t('navigation.savedRecipes')}</span>
                </MobileQuickAction>
              </MobileQuickActions>
            </MobileMenuHeader>

            {/* Navigation Section */}
            <MobileSectionTitle theme={theme}>{t('mobileMenu.navigation')}</MobileSectionTitle>
            <AnimatedMenuContainer
              initial="hidden"
              animate={isMobileMenuOpen ? "visible" : "hidden"}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.03,
                    delayChildren: 0.1
                  }
                }
              }}
            >
              <MobileMenuItem 
                theme={theme} 
                onClick={() => handleMobileNavigation('/')}
                variants={{
                  hidden: { opacity: 0, x: 20 },
                  visible: { opacity: 1, x: 0 }
                }}
                transition={{ duration: 0.15 }}
              >
                <FiHome /> {t('navigation.home')}
              </MobileMenuItem>
              <MobileMenuItem 
                theme={theme} 
                onClick={() => handleMobileNavigation('/categories')}
                variants={{
                  hidden: { opacity: 0, x: 20 },
                  visible: { opacity: 1, x: 0 }
                }}
                transition={{ duration: 0.15 }}
              >
                <FiGrid /> {t('mobileMenu.categories')}
              </MobileMenuItem>
              <MobileMenuItem 
                theme={theme} 
                onClick={() => handleMobileNavigation('/meal-planner')}
              >
                <span style={{ fontSize: 18 }}>📅</span> {currentLanguage === 'uk' ? 'Меню на 7 днів' : '7-Day Meal Planner'}
              </MobileMenuItem>
              <MobileMenuItem 
                theme={theme} 
                onClick={() => { handleMenuItemClick('profile'); closeMobileMenu(); }}
                variants={{
                  hidden: { opacity: 0, x: 20 },
                  visible: { opacity: 1, x: 0 }
                }}
                transition={{ duration: 0.15 }}
              >
                <FiUser /> {t('navigation.profile')}
              </MobileMenuItem>
              <MobileMenuItem 
                theme={theme} 
                onClick={() => { handleMenuItemClick('saved'); closeMobileMenu(); }}
                variants={{
                  hidden: { opacity: 0, x: 20 },
                  visible: { opacity: 1, x: 0 }
                }}
                transition={{ duration: 0.15 }}
              >
                <FiHeart /> {t('mobileMenu.saved')}
              </MobileMenuItem>
            </AnimatedMenuContainer>

            <MobileMenuDivider theme={theme} />

            {/* Account Section */}
            <MobileSectionTitle theme={theme}>{t('mobileMenu.account')}</MobileSectionTitle>
            <AnimatedMenuContainer
              initial="hidden"
              animate={isMobileMenuOpen ? "visible" : "hidden"}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.03,
                    delayChildren: 0.2
                  }
                }
              }}
            >
              <MobileMenuItem 
                theme={theme} 
                onClick={() => { handleMenuItemClick('settings'); closeMobileMenu(); }}
                variants={{
                  hidden: { opacity: 0, x: 20 },
                  visible: { opacity: 1, x: 0 }
                }}
                transition={{ duration: 0.15 }}
              >
                <FiSettings /> {t('navigation.settings')}
              </MobileMenuItem>
              {user && (user.roleId === 4 || user.roleId === 3) && (
                <MobileMenuItem 
                  theme={theme} 
                  onClick={() => handleMobileNavigation('/admin')}
                  variants={{
                    hidden: { opacity: 0, x: 20 },
                    visible: { opacity: 1, x: 0 }
                  }}
                  transition={{ duration: 0.15 }}
                >
                  <MdAdminPanelSettings /> {t('navigation.adminPanel')}
                </MobileMenuItem>
              )}
              {user && (
                <MobileMenuItem 
                  theme={theme} 
                  onClick={() => handleMobileNavigation('/premium')}
                  variants={{
                    hidden: { opacity: 0, x: 20 },
                    visible: { opacity: 1, x: 0 }
                  }}
                  transition={{ duration: 0.15 }}
                >
                  <FiStar /> Premium
                </MobileMenuItem>
              )}
            </AnimatedMenuContainer>

            <MobileMenuDivider theme={theme} />

            {/* Settings Section */}
            <MobileSectionTitle theme={theme}>{t('mobileMenu.settings')}</MobileSectionTitle>
            <MobileControlsGrid>
              <MobileControlButton 
                theme={theme} 
                active={theme === 'dark'}
                onClick={toggleTheme}
              >
                {theme === 'dark' ? <FiMoon /> : <FiSun />}
                {theme === 'dark' ? 'Темна' : 'Світла'}
              </MobileControlButton>
              
              <MobileLanguageSelect
                theme={theme}
                value={currentLanguage}
                onChange={handleLanguageChange}
                aria-label={t('header.selectLanguage') || 'Select language'}
              >
                <option value="uk">🇺🇦 UA</option>
                <option value="en">🇺🇸 EN</option>
              </MobileLanguageSelect>
            </MobileControlsGrid>

            <MobileMenuDivider theme={theme} />

            {/* Support Section */}
            <MobileSectionTitle theme={theme}>{t('mobileMenu.support')}</MobileSectionTitle>
            <AnimatedMenuContainer
              initial="hidden"
              animate={isMobileMenuOpen ? "visible" : "hidden"}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    delayChildren: 0.3
                  }
                }
              }}
            >
              <MobileMenuItem 
                theme={theme} 
                onClick={handleSupportClick}
                variants={{
                  hidden: { opacity: 0, x: 20 },
                  visible: { opacity: 1, x: 0 }
                }}
                transition={{ duration: 0.15 }}
              >
                <FiMessageCircle /> {t('navigation.support')}
              </MobileMenuItem>
            </AnimatedMenuContainer>

            {/* Logout Button */}
            <MobileLogoutButton theme={theme} onClick={() => { handleSignOut(); closeMobileMenu(); }}>
              <FiLogOut />
              {t('common.signOut')}
            </MobileLogoutButton>


          </>
        ) : (
          <>
            {/* Guest Header */}
            <MobileMenuHeader theme={theme}>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <h3 style={{ 
                  margin: '0 0 8px', 
                  color: theme === 'dark' ? '#f1f5f9' : '#333',
                  fontSize: '20px',
                  fontWeight: '600'
                }}>
                  {t('mobileMenu.welcome')}
                </h3>
                <p style={{ 
                  margin: 0, 
                  color: theme === 'dark' ? '#94a3b8' : '#666',
                  fontSize: '14px'
                }}>
                  {t('mobileMenu.loginPrompt')}
                </p>
              </div>
              
              {/* Auth Actions */}
              <MobileQuickActions>
                <MobileQuickAction theme={theme} onClick={() => { handleLoginClick(); closeMobileMenu(); }}>
                  <FiUser />
                  <span>{t('navigation.login')}</span>
                </MobileQuickAction>
                <MobileQuickAction theme={theme} onClick={() => { handleRegisterClick(); closeMobileMenu(); }}>
                  <FiEdit />
                  <span>{t('mobileMenu.registration')}</span>
                </MobileQuickAction>
              </MobileQuickActions>
            </MobileMenuHeader>

            {/* Navigation Section */}
            <MobileSectionTitle theme={theme}>{t('mobileMenu.navigation')}</MobileSectionTitle>
            <AnimatedMenuContainer
              initial="hidden"
              animate={isMobileMenuOpen ? "visible" : "hidden"}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.03,
                    delayChildren: 0.1
                  }
                }
              }}
            >
              <MobileMenuItem 
                theme={theme} 
                onClick={() => handleMobileNavigation('/')}
                variants={{
                  hidden: { opacity: 0, x: 20 },
                  visible: { opacity: 1, x: 0 }
                }}
                transition={{ duration: 0.15 }}
              >
                <FiHome /> {t('navigation.home')}
              </MobileMenuItem>
              <MobileMenuItem 
                theme={theme} 
                onClick={() => handleMobileNavigation('/categories')}
                variants={{
                  hidden: { opacity: 0, x: 20 },
                  visible: { opacity: 1, x: 0 }
                }}
                transition={{ duration: 0.15 }}
              >
                <FiGrid /> {t('mobileMenu.categories')}
              </MobileMenuItem>
              <MobileMenuItem 
                theme={theme} 
                onClick={() => handleMobileNavigation('/meal-planner')}
              >
                <span style={{ fontSize: 18 }}>📅</span> {currentLanguage === 'uk' ? 'Меню на 7 днів' : '7-Day Meal Planner'}
              </MobileMenuItem>
            </AnimatedMenuContainer>

            <MobileMenuDivider theme={theme} />

            {/* Settings Section */}
            <MobileSectionTitle theme={theme}>{t('mobileMenu.settings')}</MobileSectionTitle>
            <MobileControlsGrid>
              <MobileControlButton 
                theme={theme} 
                active={theme === 'dark'}
                onClick={toggleTheme}
              >
                {theme === 'dark' ? <FiMoon /> : <FiSun />}
                {theme === 'dark' ? 'Темна' : 'Світла'}
              </MobileControlButton>
              
              <MobileLanguageSelect
                theme={theme}
                value={currentLanguage}
                onChange={handleLanguageChange}
                aria-label={t('header.selectLanguage') || 'Select language'}
              >
                <option value="uk">🇺🇦 UA</option>
                <option value="en">🇺🇸 EN</option>
              </MobileLanguageSelect>
            </MobileControlsGrid>
          </>
        )}
          </MobileNav>
        )}

      {showLoginModal && (
        <Suspense fallback={null}>
          <LoginModal
            onClose={closeModals}
            onRegisterClick={openRegisterModal}
            theme={theme}
          />
        </Suspense>
      )}
      {showRegisterModal && (
        <Suspense fallback={null}>
          <RegisterModal onClose={closeModals} onLoginClick={openLoginModal} theme={theme} />
        </Suspense>
      )}
    </HeaderContainer>
  );
};

export default React.memo(Header);